import React, { useEffect, useRef } from 'react';
import { Loader2 } from 'lucide-react';
import { API_BASE_URL } from '../../utils/constants';
import { handleAuthError } from '../../utils/authUtils';

const API_URL = API_BASE_URL;

const IMAGE_LABELS = {
    satellite_aerial: '🛰️ Satellite Aerial',
    area_context: '🏘️ Area Context',
    junction_closeup: '🔍 Junction Close-up',
    traffic_map: '🚦 Traffic & Competitors',
    street_view: '📸 Street View',
    competition_map: '🏪 Competition Map',
};

const SCORE_LABELS = {
    visibility: { label: 'Site Visibility', color: '#3b82f6' },
    road_access: { label: 'Road Access / Ingress-Egress', color: '#10b981' },
    lot_shape_size: { label: 'Lot Shape & Size', color: '#f59e0b' },
    surrounding_land_use: { label: 'Surrounding Land Use', color: '#8b5cf6' },
    road_type_speed: { label: 'Road Type & Speed', color: '#ef4444' },
    development_potential: { label: 'Development Potential', color: '#06b6d4' },
};

const inferImageMediaType = (base64Image) => {
    if (!base64Image) return 'image/jpeg';

    if (base64Image.startsWith('/9j/')) {
        return 'image/jpeg';
    }
    if (base64Image.startsWith('iVBORw0KGgo')) {
        return 'image/png';
    }
    if (base64Image.startsWith('R0lGOD')) {
        return 'image/gif';
    }
    if (base64Image.startsWith('UklGR')) {
        return 'image/webp';
    }

    return 'image/jpeg';
};

const ScoreBar = ({ label, score, color }) => (
    <div style={{ marginBottom: '12px' }}>
        <div style={{ display: 'flex', justifyContent: 'space-between', marginBottom: '4px' }}>
            <span style={{ fontSize: '14px', fontWeight: '500', color: '#374151' }}>{label}</span>
            <span style={{ fontSize: '14px', fontWeight: '700', color }}>{score}/10</span>
        </div>
        <div style={{ height: '8px', borderRadius: '4px', background: '#e5e7eb', overflow: 'hidden' }}>
            <div
                style={{
                    width: `${(score / 10) * 100}%`,
                    height: '100%',
                    borderRadius: '4px',
                    background: color,
                    transition: 'width 0.6s ease',
                }}
            />
        </div>
    </div>
);

const SiteIntelligenceTab = ({
    address,
    detailedData,
    trafficData,
    getSmarty,
    siteImages,
    setSiteImages,
    siteAnalysis,
    setSiteAnalysis,
    siteLoading,
    setSiteLoading,
    getAuthHeaders,
}) => {
    const requestInFlightRef = useRef(false);

    useEffect(() => {
        const fetchSiteIntelligence = async () => {
            if (siteImages || requestInFlightRef.current) return;

            requestInFlightRef.current = true;
        const smarty = getSmarty();
        const lat = smarty?.location_info?.latitude || trafficData?.coordinates?.lat;
        const lon = smarty?.location_info?.longitude || trafficData?.coordinates?.lon;
            if (!lat || !lon) {
                requestInFlightRef.current = false;
                return;
            }

            setSiteLoading(true);
            try {
            // Build sensors list from traffic data
            const sensors = (trafficData?.all_nearby_sensors || []).map(s => ({
                lat: s.latitude || s.lat,
                lon: s.longitude || s.lon,
                aadt: s.aadt || s.annual_average_daily_traffic,
                label: s.route_name || s.road_name || '',
            }));

            // Build competitors list
            const gasStations = detailedData?.competitors_data?.gas_stations?.results || [];
            const competitors = gasStations.map(c => ({
                lat: c.geometry?.location?.lat || c.lat,
                lon: c.geometry?.location?.lng || c.lon,
                name: c.name || '',
            }));

            const resp = await fetch(`${API_URL}/satellite-analysis`, {
                method: 'POST',
                headers: {
                    'Content-Type': 'application/json',
                    ...getAuthHeaders(),
                },
                body: JSON.stringify({ address, lat, lon, sensors, competitors }),
            });

            if (resp.status === 401) {
                handleAuthError('Your session has expired. Please login again.');
                return;
            }

            if (resp.ok) {
                const data = await resp.json();
                setSiteImages(data.images || {});
                setSiteAnalysis(data.analysis || null);
            } else {
                console.error('Site intelligence error', await resp.text());
            }
            } catch (e) {
            console.error('Site intelligence fetch failed', e);
            } finally {
                requestInFlightRef.current = false;
                setSiteLoading(false);
            }
        };

        fetchSiteIntelligence();
    }, [address, detailedData, trafficData, getSmarty, siteImages, setSiteImages, setSiteAnalysis, setSiteLoading, getAuthHeaders]);

    if (siteLoading) {
        return (
            <div style={{ display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', height: '400px', gap: '16px' }}>
                <Loader2 size={40} style={{ animation: 'spin 1s linear infinite', color: '#3b82f6' }} />
                <div style={{ fontSize: '18px', color: '#6b7280' }}>Capturing satellite & street view images...</div>
                <div style={{ fontSize: '14px', color: '#9ca3af' }}>Running Claude Vision site assessment</div>
                <style>{`@keyframes spin { to { transform: rotate(360deg); } }`}</style>
            </div>
        );
    }

    if (!siteImages) {
        return (
            <div style={{ textAlign: 'center', padding: '60px 20px', color: '#6b7280' }}>
                <div style={{ fontSize: '48px', marginBottom: '16px' }}>🛰️</div>
                <div style={{ fontSize: '18px', marginBottom: '8px' }}>Site Intelligence unavailable</div>
                <div style={{ fontSize: '14px' }}>Coordinates are needed to capture imagery.</div>
            </div>
        );
    }

    const scores = siteAnalysis?.scores || {};
    const hasAnalysis = siteAnalysis && !siteAnalysis.error && !siteAnalysis.parse_error;

    return (
        <div>
            {/* Image Grid */}
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1f2937', marginBottom: '20px', borderBottom: '3px solid #3b82f6', paddingBottom: '8px' }}>
                🛰️ Property Imagery
            </h2>
            <div style={{ display: 'grid', gridTemplateColumns: 'repeat(3, 1fr)', gap: '16px', marginBottom: '40px' }}>
                {Object.entries(IMAGE_LABELS).map(([key, label]) => {
                    const b64 = siteImages[key];
                    return (
                        <div key={key} style={{ borderRadius: '12px', overflow: 'hidden', border: '1px solid #e5e7eb', background: '#f9fafb' }}>
                            <div style={{ padding: '10px 12px', fontSize: '13px', fontWeight: '600', color: '#374151', background: '#f3f4f6', borderBottom: '1px solid #e5e7eb' }}>
                                {label}
                            </div>
                            {b64 ? (
                                <img
                                    src={`data:${inferImageMediaType(b64)};base64,${b64}`}
                                    alt={label}
                                    style={{ width: '100%', height: '240px', objectFit: 'cover', display: 'block' }}
                                />
                            ) : (
                                <div style={{ width: '100%', height: '240px', display: 'flex', alignItems: 'center', justifyContent: 'center', color: '#9ca3af', fontSize: '14px' }}>
                                    Image unavailable
                                </div>
                            )}
                        </div>
                    );
                })}
            </div>

            {/* AI Assessment */}
            <h2 style={{ fontSize: '22px', fontWeight: '700', color: '#1f2937', marginBottom: '20px', borderBottom: '3px solid #10b981', paddingBottom: '8px' }}>
                🤖 AI Site Assessment {hasAnalysis && siteAnalysis.overall_score && (
                    <span style={{ float: 'right', fontSize: '18px', fontWeight: '800', color: siteAnalysis.overall_score >= 7 ? '#10b981' : siteAnalysis.overall_score >= 5 ? '#f59e0b' : '#ef4444' }}>
                        Overall: {siteAnalysis.overall_score}/10
                    </span>
                )}
            </h2>

            {hasAnalysis ? (
                <div style={{ display: 'grid', gridTemplateColumns: '1fr 1fr', gap: '32px' }}>
                    {/* Scores */}
                    <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '24px', border: '1px solid #e5e7eb' }}>
                        <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '16px' }}>Score Breakdown</h3>
                        {Object.entries(SCORE_LABELS).map(([key, { label, color }]) => (
                            <ScoreBar key={key} label={label} score={scores[key] || 0} color={color} />
                        ))}

                        {/* Meta info */}
                        <div style={{ marginTop: '20px', padding: '12px', background: '#fff', borderRadius: '8px', border: '1px solid #e5e7eb', fontSize: '13px', color: '#6b7280' }}>
                            {siteAnalysis.road_classification && <div><strong>Road:</strong> {siteAnalysis.road_classification}</div>}
                            {siteAnalysis.estimated_speed_limit && <div><strong>Speed:</strong> {siteAnalysis.estimated_speed_limit}</div>}
                            {siteAnalysis.intersection_type && <div><strong>Intersection:</strong> {siteAnalysis.intersection_type}</div>}
                        </div>
                    </div>

                    {/* Narrative + Strengths/Concerns */}
                    <div>
                        {siteAnalysis.narrative && (
                            <div style={{ background: '#f9fafb', borderRadius: '12px', padding: '24px', border: '1px solid #e5e7eb', marginBottom: '20px' }}>
                                <h3 style={{ fontSize: '16px', fontWeight: '600', color: '#1f2937', marginBottom: '12px' }}>Professional Assessment</h3>
                                <p style={{ fontSize: '14px', lineHeight: '1.7', color: '#374151', whiteSpace: 'pre-wrap' }}>{siteAnalysis.narrative}</p>
                            </div>
                        )}

                        {siteAnalysis.strengths?.length > 0 && (
                            <div style={{ background: '#f0fdf4', borderRadius: '12px', padding: '20px', border: '1px solid #bbf7d0', marginBottom: '16px' }}>
                                <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#166534', marginBottom: '8px' }}>✅ Strengths</h4>
                                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                    {siteAnalysis.strengths.map((s, i) => (
                                        <li key={i} style={{ fontSize: '14px', color: '#15803d', marginBottom: '4px' }}>{s}</li>
                                    ))}
                                </ul>
                            </div>
                        )}

                        {siteAnalysis.concerns?.length > 0 && (
                            <div style={{ background: '#fef2f2', borderRadius: '12px', padding: '20px', border: '1px solid #fecaca' }}>
                                <h4 style={{ fontSize: '15px', fontWeight: '600', color: '#991b1b', marginBottom: '8px' }}>⚠️ Concerns</h4>
                                <ul style={{ margin: 0, paddingLeft: '20px' }}>
                                    {siteAnalysis.concerns.map((c, i) => (
                                        <li key={i} style={{ fontSize: '14px', color: '#b91c1c', marginBottom: '4px' }}>{c}</li>
                                    ))}
                                </ul>
                            </div>
                        )}
                    </div>
                </div>
            ) : (
                <div style={{ background: '#fffbeb', borderRadius: '12px', padding: '24px', border: '1px solid #fde68a', textAlign: 'center' }}>
                    <div style={{ fontSize: '16px', fontWeight: '500', color: '#92400e', marginBottom: '8px' }}>
                        {siteAnalysis?.error || 'AI analysis unavailable'}
                    </div>
                    <div style={{ fontSize: '13px', color: '#a16207' }}>
                        Images are available above. Set ANTHROPIC_API_KEY for Claude Vision analysis.
                    </div>
                </div>
            )}
        </div>
    );
};

export default SiteIntelligenceTab;
